import { Button, CreateButton, Drawer, ExportButton, Icons, List, Space, useTable } from "@pankod/refine-antd";
import { CanAccess, CrudFilters, HttpError, IResourceComponentsProps, useExport, useOne } from "@pankod/refine-core";
import { CloudScreeningFilter } from "components/cloudscreening/cloudscreeningfilter";
import { RenderWorkflowModal } from "components/common";
import { ICloudScreening, ICloudScreeningFilterVariables, IScreeningMetadata, IState } from "interfaces";
import { useEffect, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_EXPORT, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, RESOURCE_PATH, STALE_DURATION } from "scripts/site";
import { CloudScreeningTable } from "./cloudScreeningTable";

export const CloudScreeningList: React.FC<IResourceComponentsProps> = () => {
  // Fetch entity metadata using useOne hook
  // This hook retrieves metadata for the cloud screening entity
  // It uses the DATAPROVIDER_LOOKUP to get the metadata from the specified resource path
  // The id is set to RESOURCE_PATH.CLOUD_SCREENING, which is a constant defined
  const { data: entityMetadata, isLoading } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.CLOUD_SCREENING,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION
    }
  });

  const entityStates = entityMetadata?.data?.states as IState[] || []; // Assign it directly

  // State to manage filter form visibility, asset view, and data fetching
  const [filterOpen, switchFilterForm] = useState(false);
  const [fetchData, switchFetchData] = useState(false);

  // Fetch table data using useTable hook
  // This hook retrieves data for the cloud screening list
  // It uses the DATAPROVIDER_READ to get the data from the specified resource path
  const { tableProps, tableQueryResult, searchFormProps, sorter, filters } =
    useTable<ICloudScreening, HttpError, ICloudScreeningFilterVariables>({
      dataProviderName: DATAPROVIDER_READ,
      resource: RESOURCE_PATH.CLOUD_SCREENING,
      initialSorter: [
        {
          field: "updatedAt",
          order: "desc",
        },
      ],
      onSearch: (params) => {
        const filters: CrudFilters = [];
        const { q, associateId, managerUserId, physicalLocationId, cloudOrganizationId, state, } = params;
        filters.push({
          field: "q",
          operator: "contains",
          value: q,
        });
        filters.push({
          field: "cloudOrganizationId",
          operator: "eq",
          value: cloudOrganizationId,
        });
        filters.push({
          field: "associateId",
          operator: "eq",
          value: associateId,
        });
        filters.push({
          field: "managerUserId",
          operator: "eq",
          value: managerUserId,
        });

        filters.push({
          field: "physicalLocationId",
          operator: "eq",
          value: physicalLocationId,
        });

        filters.push({
          field: "stateManager.state",
          operator: "eq",
          value: state,
        });

        return filters;
      },
      queryOptions: {
        enabled: fetchData,
      },
    });

  let isFiltered =
    filters &&
    filters.length > 0 &&
    filters.some((fltr) => fltr.value !== undefined);

  const clearFilters = () => {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  };

  const metaConfig = entityMetadata?.data?.config;

  // Effect to trigger data fetching when the component mounts or when certain conditions change
  useEffect(() => {
    if (!isLoading) {
      switchFetchData(true);
    }
  }, [isLoading]);
  const totalRequests = tableQueryResult?.data?.total ?? 0;

  const { triggerExport, isLoading: exportLoading } = useExport<any>({
    dataProviderName: DATAPROVIDER_EXPORT,
    resourceName: RESOURCE_PATH.CLOUD_SCREENING,
    pageSize: metaConfig?.export?.pageSize ?? 100,
    sorter: [
      {
        field: "updatedAt",
        order: "desc"
      }
    ],
  });

  return (
    <List
      headerProps={{
        title:
          (tableQueryResult.isLoading ? "Loading" : totalRequests) +
          " Cloud Screening Request" +
          (totalRequests !== 1 ? "s" : ""),
        tags: (
          <>
            <Button
              key="filter"
              type="default"
              icon={
                isFiltered ? <Icons.FilterFilled /> : <Icons.FilterOutlined />
              }
              style={{ paddingRight: isFiltered ? 4 : 15 }}
              title={
                isFiltered
                  ? "You are viewing a filtered list"
                  : entityMetadata?.data?.config?.search?.textMessages
                    ?.buttonText || "Search & Filter"
              }
              onClick={() => switchFilterForm(true)}
            >
              Search
              {isFiltered && (
                <>
                  <Button
                    type="text"
                    danger
                    title={"Clear Filters"}
                    size="small"
                    style={{ marginLeft: 5 }}
                    onClick={(e) => {
                      clearFilters();
                      e.stopPropagation();
                    }}
                    icon={<Icons.CloseOutlined />}
                  ></Button>
                </>
              )}
            </Button>
          </>
        ),
        extra: (
          <Space>
            <CanAccess resource={RESOURCE_PATH.CLOUD_SCREENING} action="create" params={{ id: "1", dataProviderName: DATAPROVIDER_CREATE }}>
              <CreateButton icon={<Icons.PlusOutlined />}>Add</CreateButton>
            </CanAccess>
            {metaConfig?.export?.enabled && (
              <CanAccess
                key={RESOURCE_PATH.CLOUD_SCREENING}
                resource={RESOURCE_PATH.CLOUD_SCREENING}
                action="list"
                params={{ dataProviderName: DATAPROVIDER_EXPORT }}
              >
                <ExportButton onClick={triggerExport} loading={exportLoading} />
              </CanAccess>
            )}
            {RenderWorkflowModal(entityStates)}
          </Space>
        ),
      }}
    >
      <CloudScreeningTable
        metadata={entityMetadata?.data}
        cloudScreeningData={tableProps}
        sorter={sorter || []}
      />
      <Drawer
        title={
          entityMetadata?.data?.config?.search?.textMessages
            ?.drawerFormHeader || "Search & Filter"
        }
        placement="right"
        size="default"
        open={filterOpen}
        onClose={() => {
          switchFilterForm(false);
        }}
      >
        {entityMetadata?.data && (
          <CloudScreeningFilter
            formProps={searchFormProps}
            onApplyFilter={() => {
              switchFilterForm(false);
              searchFormProps.form?.submit();
            }}
          />
        )}
      </Drawer>
    </List>
  );
};
